import { makeStyles } from "@mui/styles";
import { colors } from "../../../styles/colors";
import { down } from "../../../styles/breakpoints";

export default makeStyles({
  // Dialog styles
  dialogPaper: {
    borderRadius: "12px",
    maxWidth: "600px",
    width: "100%",
    margin: "16px",
  },
  
  dialogTitle: {
    padding: "24px 24px 16px 24px",
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#333",
    borderBottom: "1px solid #e0e0e0",
  },
  
  dialogContent: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  
  dialogActions: {
    padding: "16px 24px 24px 24px",
    gap: "12px",
    borderTop: "1px solid #e0e0e0",
  },

  // Selected signs info
  selectedSignsInfo: {
    padding: "12px 16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  
  selectedSignsText: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#666",
  },

  // Select all section
  selectAllContainer: {
    padding: "12px 0",
    borderBottom: "1px solid #e0e0e0",
  },
  
  selectAllLabel: {
    margin: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "1rem",
      fontWeight: 500,
      color: "#333",
    },
  },

  // Services list
  servicesContainer: {
    marginTop: "8px",
    width: "100%",
  },
  
  servicesList: {
    maxHeight: "350px",
    overflowY: "auto",
    padding: "8px 0",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#fff",
    
    // Custom scrollbar
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f5f5f5",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#999",
      },
    },
  },
  
  serviceItem: {
    display: "block",
    margin: 0,
    padding: "12px 16px",
    borderBottom: "1px solid #e0e0e0",
    transition: "background-color 0.2s ease",
    
    "&:last-child": {
      borderBottom: "none",
    },
    
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    
    "& .MuiFormControlLabel-label": {
      width: "100%",
    },
  },
  
  serviceItemSelected: {
    backgroundColor: colors.primary + "08" || "#1976d208",
    borderLeft: `3px solid ${colors.primary || "#1976d2"}`,
  },
  
  serviceContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  
  serviceName: {
    fontSize: "1rem",
    fontWeight: 500,
    color: "#333",
    lineHeight: 1.2,
  },
  
  serviceDescription: {
    fontSize: "0.875rem",
    color: "#666",
    lineHeight: 1.3,
    marginTop: "4px",
  },
  
  serviceCode: {
    fontSize: "0.75rem",
    color: "#999",
    fontStyle: "italic",
    fontFamily: "monospace",
  },

  // Loading and empty states
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 0",
  },
  
  loadingText: {
    fontSize: "1rem",
    color: "#666",
  },
  
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 20px",
    textAlign: "center",
  },
  
  emptyStateText: {
    fontSize: "1rem",
    color: "#666",
    marginTop: "8px",
  },
  
  emptyStateIcon: {
    fontSize: "48px",
    color: "#ccc",
  },

  // Action buttons
  cancelButton: {
    color: "#666",
    borderColor: "#e0e0e0",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#666",
    },
  },
  
  assignButton: {
    backgroundColor: colors.primary || "#1976d2",
    color: "#fff",
    fontWeight: 600,
    minWidth: "140px",
    "&:hover": {
      backgroundColor: colors.darkblack || "#1565c0",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      color: "#ccc",
    },
  },

  // Selection summary
  selectionSummary: {
    marginTop: "16px",
    padding: "12px 16px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #2196f3",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  
  selectionSummaryText: {
    fontSize: "0.875rem",
    color: "#1976d2",
    fontWeight: 500,
  },
  
  selectionCount: {
    backgroundColor: "#2196f3",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    minWidth: "20px",
    textAlign: "center",
  },

  // Checkbox customization
  checkbox: {
    color: "#666",
    "&.Mui-checked": {
      color: colors.primary || "#1976d2",
    },
    "&.MuiCheckbox-indeterminate": {
      color: colors.primary || "#1976d2",
    },
  },
  
  selectAllCheckbox: {
    color: "#666",
    "&.Mui-checked": {
      color: colors.primary || "#1976d2",
    },
    "&.MuiCheckbox-indeterminate": {
      color: "#ff9800",
    },
  },

  // Responsive adjustments
  [down.sm]: {
    dialogPaper: {
      margin: "8px",
      /* `90vh` ignores the mobile browser chrome, so the dialog could sit
         partly behind the URL bar. `--app-height` resolves to 100dvh where
         the browser supports it. */
      maxHeight: "calc(var(--app-height) - 16px)",
    },
    
    dialogTitle: {
      padding: "16px 16px 12px 16px",
      fontSize: "1.125rem",
    },
    
    dialogContent: {
      padding: "16px",
    },
    
    dialogActions: {
      padding: "12px 16px 16px 16px",
      flexDirection: "column-reverse",
      gap: "8px",
      
      "& .MuiButton-root": {
        width: "100%",
      },
    },
    
    servicesList: {
      maxHeight: "250px",
    },
  },
});